import React from "react";
import _ from "lodash";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import Input from "@amzn/meridian/input";
import Link from "@amzn/meridian/link";
import { formatStatus } from "../../table/utils";
import { formatNumber } from "../../../utils/formattingUtils";

interface IOverallSummaryProps {
    data: any;
    isEditing: boolean;
    handleUpdateData: any;
    year: string;
    onClickOpen: any;
    setAttachmentYear: any;
}

interface RowConfig {
    label: string;
    key: string;
    backgroundColor?: string;
    prefix?: string;
    suffix?: string;
}

interface CellConfig {
    key: string;
    isEditable: boolean;
}

const HEADER_COLUMN_WIDTH = 250;

const rowConfigs: RowConfig[] = [
    { label: "Current Status", key: "currentStatus", backgroundColor: "secondary" },
    { label: "Land", key: "land", backgroundColor: "secondary", prefix: "$ " },
    { label: "Building", key: "building", backgroundColor: "secondary", prefix: "$ " },
    { label: "Other", key: "other", backgroundColor: "secondary", prefix: "$ " },
    { label: "Total value", key: "totalValue", backgroundColor: "secondary", prefix: "$ " },
    { label: "Ratio", key: "ratio", backgroundColor: "secondary", suffix: "%" },
    { label: "Taxable value", key: "taxableValue", backgroundColor: "secondary", prefix: "$ " },
    {
        label: "Effective tax rate",
        key: "effectiveTaxRate",
        backgroundColor: "secondary",
        suffix: "%",
    },
    { label: "Taxes", key: "taxes", backgroundColor: "secondary", prefix: "$ " },
    { label: "Incentive type", key: "incentiveType", backgroundColor: "secondary", prefix: "$ " },
    { label: "Attachments", key: "attachments", backgroundColor: "secondary", prefix: "$ " },
    { label: "Cost", key: "cost", backgroundColor: "secondary", prefix: "$ " },
    { label: "Income", key: "income", backgroundColor: "secondary", prefix: "$ " },
    { label: "Sales", key: "sales", backgroundColor: "secondary", prefix: "$ " },
];

export const OverallSummary: React.FC<IOverallSummaryProps> = ({
    data,
    isEditing,
    handleUpdateData,
    year,
    onClickOpen,
    setAttachmentYear,
}) => {
    const cellConfigs: CellConfig[] = [
        { key: "assessment", isEditable: false },
        { key: `previousAppeals.${parseInt(year) - 1}`, isEditable: false },
        { key: `previousAppeals.${parseInt(year) - 2}`, isEditable: false },
        { key: `previousAppeals.${parseInt(year) - 3}`, isEditable: false },
    ];

    const getValue = (rowKey: string, cellKey: string) => {
        return _.get(
            data,
            `appealDetails.appealApproachCalculationData.summary.${cellKey}.${rowKey}`,
            ""
        );
    };

    const renderCell = (rowConfig: RowConfig, cellConfig: CellConfig) => {
        const { key: rowKey, prefix = "", suffix = "" } = rowConfig;
        const value = getValue(rowKey, cellConfig.key);

        if (rowKey === "currentStatus") return formatStatus(value);
        if (rowKey === "attachments")
            return (
                <>
                    <Link
                        onClick={() => {
                            if (cellConfig.key === "assessment") {
                                setAttachmentYear(year);
                            } else {
                                const match = cellConfig.key.match(/previousAppeals\.(\d+)/);
                                setAttachmentYear(match[1]);
                            }
                            onClickOpen();
                        }}
                    >
                        View
                    </Link>
                </>
            );

        if (isEditing && cellConfig.isEditable) {
            return (
                <Input
                    value={value}
                    onChange={newValue =>
                        handleUpdateData(
                            `appealDetails.appealApproachCalculationData.summary.${cellConfig.key}.${rowKey}`,
                            newValue
                        )
                    }
                    prefix={prefix}
                    suffix={suffix}
                />
            );
        }

        return value === "" ? value : `${prefix}${formatNumber(value) || 0}${suffix}`;
    };

    return (
        <>
            <Table showDividers={true} spacing="small" headerColumns={1} headerRows={1}>
                <TableRow>
                    <TableCell>Year</TableCell>
                    <TableCell>{`${year} Assessment`}</TableCell>
                    {[1, 2, 3].map(i => (
                        <TableCell key={i}>{parseInt(year) - i}</TableCell>
                    ))}
                </TableRow>
                {rowConfigs.map(rowConfig => (
                    <TableRow key={rowConfig.key} highlightOnHover={false}>
                        <TableCell
                            width={HEADER_COLUMN_WIDTH}
                            backgroundColor={rowConfig.backgroundColor}
                        >
                            {rowConfig.label}
                        </TableCell>
                        {cellConfigs.map(cellConfig => (
                            <TableCell key={cellConfig.key}>
                                {renderCell(rowConfig, cellConfig)}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </Table>
        </>
    );
};
